import axiosInstance from "@/setup/axios/axiosInstance";

import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';

// Interface for transaction filters
export interface TransactionFilters {
  company: string;
  dateFrom: string;
  dateTo: string;
  documentDateFrom: string;
  documentDateTo: string;
  moneyFlow: string;
  transactionTypeCategory: string; // 'income' or 'expense'
  account: string;
  orderCode: string;
  amountFrom: string;
  amountTo: string;
  totalValueFrom: string;
  totalValueTo: string;
  notes: string;
  employee: string;
  customer: string;
  supplier: string;
  creationDateFrom: string;
  creationDateTo: string;
  updaterName: string;
  orderStatus: string;
  search: string; // Add search parameter
  page: number;
  page_size: number;
  no_pagination?: boolean;
}

// Get transactions with filters
export const getTransactions = async (filters?: TransactionFilters) => {
  try {
    const params = new URLSearchParams();

    const resolvedCompanyId = String(
      (filters?.company && filters.company !== 'all' ? filters.company : (getCurrentCompanyId() || ''))
    ).trim();

    if (resolvedCompanyId) {
      params.append('company', resolvedCompanyId);
    }

    if (filters) {
      const isNoPagination = filters.no_pagination === true;

      if (isNoPagination) {
        params.append('no_pagination', 'true');
      }

      if (!isNoPagination && filters.page) {
        params.append("page", String(filters.page));
      }
      if (!isNoPagination && filters.page_size) {
        params.append("page_size", String(filters.page_size));
      }

      // Date filters - use custom filter field names
      if (filters.dateFrom) {
        params.append("date_from", filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append("date_to", filters.dateTo);
      }
      
      // Document date filters - use custom filter field names
      if (filters.documentDateFrom) {
        params.append("document_date_from", filters.documentDateFrom);
      }
      if (filters.documentDateTo) {
        params.append("document_date_to", filters.documentDateTo);
      }
      
      // Creation date filters
      if (filters.creationDateFrom) {
        params.append("created_at__gte", filters.creationDateFrom);
      }
      if (filters.creationDateTo) {
        params.append("created_at__lte", filters.creationDateTo);
      }
      
      // Transaction type filter (moneyFlow)
      if (filters.moneyFlow) {
        params.append("transaction_type", filters.moneyFlow);
      }
      
      // Transaction type category filter (income/expense)
      if (filters.transactionTypeCategory) {
        params.append("transaction_type__category", filters.transactionTypeCategory);
      }
      
      // Wallet/Account filter
      if (filters.account) {
        params.append("wallet", filters.account);
      }
      
      // Order filter - check if it's a UUID or text
      if (filters.orderCode) {
        // Check if orderCode is a UUID format
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(filters.orderCode)) {
          // If it's a UUID, filter by order ID
          params.append("order", filters.orderCode);
        } else {
          // If it's text, use search (which includes order__code)
          params.append("search", filters.orderCode);
        }
      }
      
      // Amount filters
      if (filters.amountFrom) {
        params.append("actual_amount__gte", filters.amountFrom);
      }
      if (filters.amountTo) {
        params.append("actual_amount__lte", filters.amountTo);
      }
      if (filters.totalValueFrom) {
        params.append("total_amount__gte", filters.totalValueFrom);
      }
      if (filters.totalValueTo) {
        params.append("total_amount__lte", filters.totalValueTo);
      }
      
      // Notes filter
      if (filters.notes) {
        params.append("note__icontains", filters.notes);
      }
      
      // Employee filter
      if (filters.employee) {
        params.append("employee", filters.employee);
      }
      
      // Customer filter
      if (filters.customer) {
        params.append("customer", filters.customer);
      }
      
      // Supplier filter
      if (filters.supplier) {
        params.append("supplier", filters.supplier);
      }
      
      // Updater name filter
      if (filters.updaterName) {
        params.append("updated_by__name__icontains", filters.updaterName);
      }
      
      // Order status filter
      if (filters.orderStatus && filters.orderStatus !== 'all') {
        params.append("status", filters.orderStatus);
      }
      
      // Search filter - search across multiple fields
      if (filters.search && filters.search.trim()) {
        params.append("search", filters.search.trim());
      }
    }

    const url = `/finance/transactions/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

// Get transaction by ID
export const getTransactionById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/finance/transactions/${id}/`);
    return response;
  } catch (error) {
    throw error;
  }
};

// Create transaction
export const createTransaction = async (transactionData: any) => {
  try {
    const response = await axiosInstance.post("/finance/transactions/", transactionData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update transaction
export const updateTransaction = async (id: string, transactionData: any) => {
  try {
    const response = await axiosInstance.put(`/finance/transactions/${id}/`, transactionData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete transaction
export const deleteTransaction = async (id: string) => {
  try {
    const response = await axiosInstance.delete(`/finance/transactions/${id}/`);
    return response;
  } catch (error) {
    throw error;
  }
};

// Bulk approve transactions
export const bulkApproveTransactions = async (transactionIds: string[]) => {
  try {
    const response = await axiosInstance.post("/finance/transactions/bulk_approve/", {
      transaction_ids: transactionIds
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Bulk update transaction status
export const bulkUpdateTransactionStatus = async (transactionIds: string[], status: 'pending' | 'approved') => {
  try {
    const response = await axiosInstance.post('/finance/transactions/bulk_update/', {
      transaction_ids: transactionIds,
      status,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Soft delete transactions
export const softDeleteTransactions = async (transactionIds: string[]) => {
  try {
    const response = await axiosInstance.post("/finance/transactions/soft_delete/", {
      transaction_ids: transactionIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Restore deleted transactions
export const restoreTransactions = async (transactionIds: string[]) => {
  try {
    const response = await axiosInstance.post("/finance/transactions/restore_deleted/", {
      transaction_ids: transactionIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Permanently delete transactions
export const permanentDeleteTransactions = async (transactionIds: string[]) => {
  try {
    const response = await axiosInstance.post("/finance/transactions/permanent_delete/", {
      transaction_ids: transactionIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get deleted transactions
export const getDeletedTransactions = async () => {
  try {
    const response = await axiosInstance.get("/finance/transactions/?show_deleted=true");
    return response;
  } catch (error) {
    throw error;
  }
};
